import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  LayoutDashboard,
  Search,
  User,
  Settings,
  ChevronDown,
  ChevronRight,
  LogOut,
  BarChart3,
  Building2,
  FileText,
  Inbox,
  MessageSquare,
  Users,
  X,
} from "lucide-react";
import logoAtletain from "../../utils/Logo_Atletain.png";

/**
 * App navigation. Two render modes:
 *  - `variant="static"`  (default): a fixed 256px column, shown only at ≥lg.
 *  - `variant="drawer"`: a slide-in overlay for mobile, controlled by `isOpen`.
 * Both share the exact same menu markup; only the chrome around it differs.
 * Every NavLink fires `onNavigate` so the drawer can close itself on navigation.
 */
// "Nuevo" pill on Reportes until the user has opened it once. Per-device and
// best-effort: storage can be unavailable, and then the pill simply stays.
const REPORTS_SEEN_KEY = "athletain.reports.seen";
const readReportsSeen = () => {
  try {
    return localStorage.getItem(REPORTS_SEEN_KEY) === "1";
  } catch {
    return false;
  }
};

const Sidebar = ({
  className = "",
  userRole,
  onLogout,
  variant = "static",
  isOpen = false,
  onClose,
  onNavigate,
  pendingContactCount = 0,
  unreadMessageCount = 0,
}) => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const [expandedSections, setExpandedSections] = useState({
    main: true,
    talent: true,
    profile: true,
    training: true,
  });

  const [reportsSeen, setReportsSeen] = useState(readReportsSeen);
  const onReportsPage = /^\/(athlete|scout)\/reportes/.test(location.pathname);
  useEffect(() => {
    if (!onReportsPage || reportsSeen) return;
    setReportsSeen(true);
    try {
      localStorage.setItem(REPORTS_SEEN_KEY, "1");
    } catch {
      // Private mode / blocked storage: the pill just keeps showing.
    }
  }, [onReportsPage, reportsSeen]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const allMenuSections = [
    {
      id: "admin-dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      roles: ["admin"],
      to: "/admin",
      end: true,
    },
    {
      id: "admin-users",
      label: "Usuarios",
      icon: Users,
      roles: ["admin"],
      to: "/admin/users",
    },
    {
      id: "admin-reports",
      label: "Reportes",
      icon: FileText,
      roles: ["admin"],
      to: "/admin/reportes",
    },
    {
      id: "admin-academies",
      label: "Academias",
      icon: Building2,
      roles: ["admin"],
      to: "/admin/academias",
    },
    {
      id: "assistant-athlete",
      label: "Asistente",
      icon: MessageSquare,
      roles: ["athlete"],
      to: "/athlete/assistant",
    },
    {
      id: "main-athlete",
      label: "Panel principal",
      icon: LayoutDashboard,
      roles: ["athlete"],
      to: "/athlete/dashboard",
    },
    // "Comunidad" (SocialPlatform) queda fuera de v1: hoy renderiza datos de
    // `data/mockData` (perfiles y tendencias inventados), y el contenido de
    // relleno es motivo de rechazo en App Store (Guideline 2.1 / 4.2). La
    // pantalla sigue en el repo; se vuelve a enlazar cuando use datos reales.
    {
      id: "contact-requests-athlete",
      label: "Solicitudes de contacto",
      icon: Inbox,
      roles: ["athlete"],
      to: "/athlete/contact-requests",
      badge: pendingContactCount,
    },
    {
      id: "messages-athlete",
      label: "Mensajes",
      icon: MessageSquare,
      roles: ["athlete"],
      to: "/athlete/messages",
      badge: userRole === "athlete" ? unreadMessageCount : 0,
    },
    {
      id: "reports-athlete",
      label: "Reportes",
      icon: FileText,
      roles: ["athlete"],
      to: "/athlete/reportes",
      isNew: !reportsSeen,
    },
    {
      id: "assistant-scout",
      label: "Asistente",
      icon: MessageSquare,
      roles: ["scout"],
      to: "/scout/assistant",
    },
    {
      id: "main",
      label: "Panel Coach",
      icon: LayoutDashboard,
      roles: ["scout"],
      items: [
        { label: "Resumen", to: "/scout/dashboard" },
        { label: "Datos de Contacto", to: "/scout/contact" },
      ],
    },
    {
      id: "talent",
      label: "Mis Atletas",
      icon: Search,
      roles: ["scout"],
      items: [
        {
          label: "Mis deportistas",
          to: "/scout/my-athletes",
        },
        {
          label: "Mis Búsquedas",
          to: "/scout/searches",
          matchPrefix: "/scout/searches",
        },
        {
          label: "Lista de Atletas",
          to: "/scout/athletes",
          matchPrefix: "/scout/athletes",
        },
      ],
    },
    {
      id: "reports-scout",
      label: "Reportes",
      icon: FileText,
      roles: ["scout"],
      to: "/scout/reportes",
      isNew: !reportsSeen,
    },
    {
      id: "messages-scout",
      label: "Mensajes",
      icon: MessageSquare,
      roles: ["scout"],
      to: "/scout/messages",
      badge: userRole === "scout" ? unreadMessageCount : 0,
    },
    {
      id: "profile",
      label: "Mi Perfil",
      icon: User,
      roles: ["athlete"],
      items: [{ label: "Mis Datos", to: "/athlete/profile/personal" }],
    },
    {
      id: "training",
      label: "Entrenamiento",
      icon: BarChart3,
      roles: ["athlete"],
      items: [
        { label: "Ejercicios", to: "/athlete/profile/training" },
        { label: "Nutricion", to: "/athlete/nutrition" },
      ],
    },
  ];

  const menuSections = allMenuSections.filter(
    (section) => !userRole || section.roles.includes(userRole),
  );

  const isItemActive = (item) => {
    if (item.matchPrefix) {
      return location.pathname.startsWith(item.matchPrefix);
    }
    return location.pathname === item.to;
  };

  const isDrawer = variant === "drawer";
  const handleNavigate = () => {
    if (onNavigate) onNavigate();
  };

  // Shared inner markup, identical between the static column and the drawer.
  const content = (
    <>
      {/* El drawer se abre sobre el WebView edge-to-edge de iOS: sin el inset
          superior, el logo y el título quedan debajo del reloj/notch. */}
      <div
        className="flex items-center justify-between border-b border-blue-800 p-6"
        style={
          isDrawer
            ? { paddingTop: "calc(1.5rem + env(safe-area-inset-top))" }
            : undefined
        }
      >
        <div className="flex items-center gap-3">
          <img
            src={logoAtletain}
            alt="Athletain Logo"
            className="h-10 w-10 object-contain"
          />
          <span className="text-lg font-semibold">
            {userRole === "admin"
              ? "Administración"
              : userRole === "athlete"
                ? "Mi Perfil"
                : "Panel principal"}
          </span>
        </div>
        {isDrawer && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar menú"
            className="grid h-10 w-10 place-items-center rounded-lg text-blue-200 transition-colors hover:bg-blue-800"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <div className="border-b border-blue-800 p-4">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300"
            size={18}
          />
          <input
            type="text"
            placeholder="Buscar"
            className="w-full rounded-lg border border-blue-700 bg-blue-800 py-2 pl-10 pr-4 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-4">
        {menuSections.map((section) => {
          if (section.to) {
            return (
              <div key={section.id}>
                <NavLink
                  to={section.to}
                  end={section.end}
                  onClick={handleNavigate}
                  className={({ isActive }) =>
                    `flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors duration-200 ${
                      isActive ? "bg-blue-700 text-white" : "hover:bg-blue-800"
                    }`
                  }
                >
                  <section.icon size={18} className="text-blue-200" />
                  <span className="flex-1 text-sm font-medium">
                    {section.label}
                  </span>
                  {section.isNew && (
                    <span className="ml-auto rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold tracking-wide text-white uppercase">
                      Nuevo
                    </span>
                  )}
                  {section.badge > 0 && (
                    <span className="ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-bold text-white">
                      {section.badge}
                    </span>
                  )}
                </NavLink>
              </div>
            );
          }

          return (
            <div key={section.id}>
              <button
                onClick={() => toggleSection(section.id)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors duration-200 hover:bg-blue-800"
              >
                <section.icon size={18} className="text-blue-200" />
                <span className="flex-1 text-sm font-medium">
                  {section.label}
                </span>
                {expandedSections[section.id] ? (
                  <ChevronDown size={16} className="text-blue-300" />
                ) : (
                  <ChevronRight size={16} className="text-blue-300" />
                )}
              </button>

              {expandedSections[section.id] && (
                <div className="ml-6 mt-1 space-y-1">
                  {section.items.map((item) => {
                    const active = isItemActive(item);
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={handleNavigate}
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-200 ${
                          active
                            ? "bg-blue-700 text-white"
                            : "text-blue-200 hover:bg-blue-800"
                        }`}
                      >
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="space-y-1 border-t border-blue-800 p-4">
        {/* Admin no tiene pantalla de Configuración propia en v1; el resto sí. */}
        {userRole !== "admin" && (
          <NavLink
            to={userRole === "scout" ? "/scout/settings" : "/athlete/settings"}
            onClick={handleNavigate}
            className={({ isActive }) =>
              `flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors duration-200 ${
                isActive ? "bg-blue-700 text-white" : "hover:bg-blue-800"
              }`
            }
          >
            <Settings size={18} className="text-blue-200" />
            <span className="text-sm font-medium">Configuración</span>
          </NavLink>
        )}
        <button
          onClick={onLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-blue-200 transition-colors duration-200 hover:bg-red-600 hover:text-white"
        >
          <LogOut size={18} />
          <span className="text-sm font-medium">Cerrar Sesion</span>
        </button>
      </div>
    </>
  );

  if (!isDrawer) {
    return (
      <aside
        className={`hidden h-screen w-64 flex-col bg-blue-900 text-white lg:flex ${className}`}
      >
        {content}
      </aside>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className={`lg:hidden ${className}`}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/40"
          />
          <motion.aside
            initial={{ x: reduceMotion ? 0 : "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: reduceMotion ? 0 : "-100%" }}
            transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: reduceMotion ? 0 : 0.3 }}
            className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-blue-900 text-white shadow-2xl"
          >
            {content}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;
